import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container, Typography, CircularProgress, Alert,
  Box, Grid, Paper, Divider, List, ListItem, ListItemText, ListItemIcon,
  useTheme, useMediaQuery, Card, CardContent, CardActions, Button,
  Chip, Avatar, Pagination
} from "@mui/material";
import {
  Description as DescriptionIcon,
  Person as PersonIcon,
  BusinessCenter as BusinessCenterIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Verified as VerifiedIcon
} from "@mui/icons-material";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EmployeeCertificateView = () => {
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(5); // Show 5 certificates per page
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchCertificates = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;

        // Get current user info
        const userResponse = await axios.get(`${API_BASE_URL}/api/user/current?company_id=${company_id}`, 
          { withCredentials: true }
        );

        if (!userResponse.data || !userResponse.data.employee_id) {
          throw new Error("Couldn't retrieve employee ID from session");
        }

        const employeeId = userResponse.data.employee_id;

        // Fetch certificates
        const certsResponse = await axios.get(
          `${API_BASE_URL}/api/bonafide/employee/${employeeId}?page=${page}&limit=${limit}`,
          { headers: {
            Authorization: `Bearer ${token}`
          },
           withCredentials: true }
        );

        // API returns { data, pagination: { page, limit, total, totalPages } }
        if (certsResponse.data && certsResponse.data.data) {
          setCertificates(certsResponse.data.data);
          setTotalPages(certsResponse.data.pagination?.totalPages || 1);
          
          if (certsResponse.data.data.length > 0 && certsResponse.data.data[0].personal) {
            setEmployeeDetails(certsResponse.data.data[0].personal);
          }
        } else {
          // Fallback if structure is old format
          setCertificates(certsResponse.data || []);
          setTotalPages(1);
          if (certsResponse.data.length > 0 && certsResponse.data[0].personal) {
            setEmployeeDetails(certsResponse.data[0].personal);
          }
        }
      } catch (err) {
        //console.error("Error fetching certificates:", err);
        setError("Failed to fetch your certificates. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCertificates();
  }, [page, limit]);

  const handleViewCertificate = (certificatePath) => {
    if (certificatePath) {
      const url = certificatePath.startsWith('http') ? certificatePath : `${API_BASE_URL}/${certificatePath}`;
      window.open(url, '_blank');
    }
  };

  const handleDownload = async (certificateId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await axios.get(
        `${API_BASE_URL}/api/bonafide/certificates/${certificateId}/download`,
        { responseType: 'blob',  headers: {
          Authorization: `Bearer ${token}`
        }}
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bonafide_certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      //console.error("Error downloading certificate:", error);
      alert("Failed to download certificate. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ maxWidth: "1400px", mx: 'auto', p: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 4,
          borderRadius: 2,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          mb: 4
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              mx: 'auto',
              mb: 2
            }}
          >
            <VerifiedIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight="bold"
            color="primary"
          >
            My Certificates
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            View and download your approved bonafide certificates
          </Typography>
        </Box>

        {isLoading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {!isLoading && !error && certificates.length === 0 && (
          <Alert severity="info" sx={{ mt: 2, mb: 4 }}>
            <Typography variant="body1">
              You don't have any approved bonafide certificates yet.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Once your request is approved, your certificate will appear here.
            </Typography>
          </Alert>
        )}

        {!isLoading && !error && certificates.length > 0 && (
          <Grid container spacing={3}>
            {certificates.map((cert, index) => (
              <Grid item xs={12} key={cert.id}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    },
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <CardContent>
                    {/* Certificate Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                          Bonafide Certificate #{index + 1 + (page - 1) * limit}
                        </Typography>
                      </Box>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Approved"
                        color="success"
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Certificate Details */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <PersonIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Employee Name"
                              secondary={cert.name}
                              primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                              secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <BusinessCenterIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Employee ID"
                              secondary={cert.employee_id}
                              primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                              secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <AssignmentIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Department"
                              secondary={cert.department}
                              primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                              secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <DescriptionIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Purpose"
                              secondary={cert.reason}
                              primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                              secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CalendarIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Issued On"
                              secondary={formatDate(cert.updatedAt || cert.createdAt)}
                              primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                              secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                            />
                          </ListItem>
                        </List>
                      </Grid>

                      {/* Certificate Preview/Actions */}
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          p: 3
                        }}
                      >
                        <DescriptionIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 2 }}>
                          Click below to view or download your certificate
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<VisibilityIcon />}
                            fullWidth
                            onClick={() => handleViewCertificate(cert.certificatePath)}
                            disabled={!cert.certificatePath}
                          >
                            View
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            fullWidth
                            onClick={() => handleDownload(cert.id)}
                            disabled={!cert.certificatePath}
                          >
                            Download
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeCertificateView;